import React, { useState } from 'react';

/**
 * FAQPage: A standalone page that provides
 * a short description of the application and
 * a list of frequently asked questions (FAQ).
 * Each answer is revealed when the user clicks on the question.
 */
const FAQPage: React.FC = () => {
  // Tracks which FAQ item is currently expanded
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Keep these answers aligned with the current project, file-library, and workflow features.
  const faqs = [
    {
      question: 'What file formats can I upload?',
      answer:
        'You can upload TXT, PDF, DOC, DOCX, and ODT documents, plus MP3, M4A, and WAV audio. You can also start a project from a YouTube URL.',
    },
    {
      question: 'How does a project work?',
      answer:
        'Each project is a connected flow: source file, transcript or translation, and voiceover. Open a project from Projects, then select any step to continue where you left off.',
    },
    {
      question: 'Can I upload several files for the same workflow?',
      answer:
        'Yes. Open Files, choose Upload files, and select multiple documents or audio files. They are grouped into one project, where you can choose Translate all or Voiceover all.',
    },
    {
      question: 'What is the Files library?',
      answer:
        'Files is your reusable upload library. Search by filename, project, or date, open a file’s existing project, or select files and start a new project without changing the originals.',
    },
    {
      question: 'What happens if I upload the same file twice?',
      answer:
        'The app compares the file contents, not only the filename. If the same bytes were already uploaded by you, the existing file is reused and you will see a successful upload message instead of creating another copy.',
    },
    {
      question: 'Can I edit a translation before making audio?',
      answer:
        'Yes. Open the translation step, review or edit the saved text, then continue to voiceover when it is ready. Generate the audio again after making substantial edits.',
    },
    {
      question: 'Can I choose a different voice for each project?',
      answer:
        'Yes. Add voices in the Voices page and choose one for a voiceover step. Your selected voice is saved with that project step and can be changed for a new version.',
    },
    {
      question: 'Can I create an alternate version of a project?',
      answer:
        'Yes. Open a completed source, translation, or voiceover step and choose Create a branch. The new path starts from a copy while the original project remains intact.',
    },
    {
      question: 'How do I know when processing is finished?',
      answer:
        'Project steps show their current status, including In progress, Ready to continue, Complete, and Needs attention. Projects refresh automatically while work is running, so you can leave and return later.',
    },
    {
      question: 'How do points and payments work?',
      answer:
        'Processing costs are shown before you start a task. Tasks use your available points; if a task requires payment, the project shows a payment link before processing begins.',
    },
    {
      question: 'What should I do if an upload or task fails?',
      answer:
        'Open the step marked Needs attention and follow the retry or previous-step link. You can also create a branch from a completed step to try a different language, label, or voice while keeping the original path.',
    },
  ];

  // Toggles the FAQ item expansion
  const toggleFAQ = (index: number) => {
    if (expandedIndex === index) {
      // Collapse if the same question is clicked again
      setExpandedIndex(null);
    } else {
      setExpandedIndex(index);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Short description / introduction */}
      <div className="mb-8 bg-white border border-gray-200 p-6 rounded-md shadow-sm">
        <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-center text-gray-800">
          Frequently Asked Questions
        </h1>
        <p className="text-gray-700 text-sm sm:text-base leading-relaxed text-center">
          Divine Echo keeps your uploads, translations, and voiceovers together in simple project flows.
          Find answers about uploading, batch processing, the file library, voices, and project branches.
        </p>
      </div>

      {/* FAQ List */}
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-md overflow-hidden shadow-sm"
          >
            {/* Question */}
            <button
              onClick={() => toggleFAQ(index)}
              aria-expanded={expandedIndex === index}
              className="w-full text-left px-4 py-3 bg-gray-100 focus:outline-none hover:bg-gray-200 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-gray-800 font-medium text-sm sm:text-base">
                  {faq.question}
                </span>
                {/* Simple arrow icon that rotates on expand */}
                <svg
                  className={`h-5 w-5 text-gray-600 transform transition-transform duration-300 ${
                    expandedIndex === index ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {/* Answer with smooth vertical expansion/collapse */}
            <div
                className={`px-4 bg-white text-sm sm:text-base text-gray-700 transition-all duration-300 ${
                expandedIndex === index ? 'max-h-40 py-3' : 'max-h-0 py-0'
              } overflow-hidden`}
                style={{ maxHeight: expandedIndex === index ? '500px' : '0px' }}
            >
              {faq.answer}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQPage;
